import type { Device } from '@/api/endpointsInstall';
import { CopyButton } from '@/components/CopyButton';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDate } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { appleStatusOf, deviceFacts, linkExpired, type Tone } from './devicesModel';

const TONE: Record<Tone, string> = { succeeded: 'text-succeeded', awaiting: 'text-awaiting', failed: 'text-failed', muted: 'text-subtle' };
const TONE_ICON: Record<Tone, string> = { succeeded: 'check_circle', awaiting: 'pending', failed: 'error', muted: 'schedule' };

/** The org's devices: name, model, UDID, Apple status, and Remove for those who may. */
export function DeviceRows({ devices, now, onRemove }: { devices: Device[]; now: number; onRemove: ((d: Device) => void) | null }) {
  return (
    <ul className="flex flex-col divide-y" data-testid="devices-list">
      {devices.map((d, i) => <DeviceRow key={d.id} d={d} index={i} now={now} onRemove={onRemove} />)}
    </ul>
  );
}

function DeviceRow({ d, index, now, onRemove }: { d: Device; index: number; now: number; onRemove: ((d: Device) => void) | null }) {
  const apple = appleStatusOf(d);
  const expired = linkExpired(d, now);
  return (
    <li data-testid="device-row" data-device-id={d.id} data-status={d.status} style={stagger(index)} className="enter flex flex-wrap items-center gap-3 px-4 py-3 sm:px-5">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border bg-card text-muted-foreground"><Icon name="phone_iphone" size={20} /></span>
      <div className="min-w-0 flex-1 basis-56">
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium text-foreground">{d.name}</span>
          {expired ? <Tag>link expired</Tag> : null}
        </div>
        <div className="tabular flex flex-wrap gap-x-1.5 font-mono text-xs text-subtle">
          {d.status === 'registered' ? [...deviceFacts(d), d.registeredAt ? `registered ${formatDate(d.registeredAt)}` : null].filter(Boolean).join(' · ') : 'Waiting for the device to open its registration link'}
        </div>
        {d.udid ? <UdidLine udid={d.udid} /> : null}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className={cn('flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs', TONE[apple.tone])} data-testid="device-apple-status">
            <Icon name={TONE_ICON[apple.tone]} size={14} />
            {apple.label}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{apple.hint}</TooltipContent>
      </Tooltip>
      {onRemove ? (
        <Button variant="ghost" size="icon" aria-label={`Remove ${d.name}`} className="size-11" onClick={() => onRemove(d)} data-testid="device-remove">
          <Icon name="cancel" size={18} />
        </Button>
      ) : null}
    </li>
  );
}

function UdidLine({ udid }: { udid: string }) {
  return (
    <div className="mt-0.5 flex items-center gap-1 font-mono text-[11px] text-subtle">
      <span className="shrink-0">UDID</span>
      <span className="min-w-0 truncate text-muted-foreground" data-testid="device-udid">{udid}</span>
      <CopyButton text={udid} label="Copy UDID" size="icon-sm" className="size-7" />
    </div>
  );
}
